import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies, contentTypes } from "@/mocks/movies";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/feature/MovieCard";
import { usePageMeta } from "@/hooks/usePageMeta";

const hot = [...movies].sort((a, b) => b.views - a.views).slice(0, 4);
const quickKeywords = ["爱情", "悬疑", "治愈", "国产剧", "2025"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKeyword = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(urlKeyword);
  const [type, setType] = useState("全部");

  useEffect(() => {
    setKeyword(urlKeyword);
  }, [urlKeyword]);

  usePageMeta(
    urlKeyword
      ? `搜索“${urlKeyword}”结果 - 诱人的女员工hd`
      : "搜索影视作品 - 诱人的女员工hd影视专区",
    urlKeyword
      ? `与“${urlKeyword}”相关的电影、电视剧搜索结果，含剧情简介、演员信息与上映年份，可继续按类型筛选。`
      : "输入片名、演员、题材或年份关键词，快速查找影视作品资料与剧情介绍。",
  );

  const trimmed = keyword.trim();

  const results = useMemo(() => {
    if (!trimmed) return [];
    const needle = trimmed.toLowerCase();
    return movies.filter((movie) => {
      if (type !== "全部" && movie.type !== type) return false;
      const haystack = [
        movie.title,
        movie.type,
        movie.region,
        movie.cast,
        movie.director,
        movie.summary,
        String(movie.year),
        ...movie.genres,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [trimmed, type]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (trimmed) next.set("q", trimmed);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const handleQuickSearch = (value: string) => {
    setKeyword(value);
    const next = new URLSearchParams(searchParams);
    next.set("q", value);
    setSearchParams(next, { replace: true });
  };

  return (
    <PageLayout>
      <div className="border-b border-background-200 bg-primary-950">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-primary-300">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center text-primary-500" />
            <span className="text-primary-100">搜索</span>
          </nav>

          <h1 className="mt-4 text-2xl font-bold tracking-tight text-background-50 md:text-3xl">
            {trimmed ? `搜索「${trimmed}」的结果` : "搜索影视作品"}
          </h1>
          <p className="mt-2 text-sm text-primary-200">
            输入片名、演员、题材或年份关键词，立刻找到对应的影视资料。
          </p>

          <form onSubmit={handleSubmit} className="mt-6 flex w-full max-w-2xl flex-col gap-2 sm:flex-row">
            <div className="flex h-12 flex-1 items-center gap-2 rounded-md border border-primary-700 bg-primary-900/80 px-4">
              <i className="ri-search-line w-4 h-4 flex items-center justify-center text-primary-300" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="例如：夜色微光 / 悬疑 / 林知夏"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm text-background-50 placeholder:text-primary-400 outline-none"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  aria-label="清空关键词"
                  className="flex h-6 w-6 items-center justify-center rounded-full text-primary-300 hover:bg-primary-800 cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-sm w-4 h-4 flex items-center justify-center" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="flex h-12 items-center justify-center gap-2 rounded-md bg-accent-500 px-6 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-primary-400">热门搜索</span>
            {quickKeywords.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => handleQuickSearch(item)}
                className="rounded-full border border-primary-700 px-3 py-1 text-xs text-primary-100 transition-colors hover:border-accent-500 hover:text-accent-400 cursor-pointer whitespace-nowrap"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
        {trimmed ? (
          <>
            <div className="flex flex-col gap-4 border-b border-background-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-600">
                找到 <span className="font-medium text-foreground-950">{results.length}</span> 条与「{trimmed}」相关的内容
              </p>
              <div className="flex w-full items-center gap-1 rounded-full bg-background-100 p-1 sm:w-auto">
                {contentTypes.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setType(item)}
                    className={`flex-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors cursor-pointer sm:flex-none ${
                      type === item ? "bg-primary-950 text-background-50 font-medium" : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {results.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4 xl:grid-cols-6">
                {results.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="mt-6">
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                    <i className="ri-search-line text-2xl" />
                  </span>
                  <h2 className="mt-4 text-base font-medium text-foreground-950">未找到相关内容</h2>
                  <p className="mt-2 max-w-md text-sm leading-7 text-foreground-500">
                    换个关键词试试，或者从下方热门影视中挑选一部。也可以浏览完整的影视分类页。
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    <Link
                      to="/category"
                      className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer whitespace-nowrap"
                    >
                      浏览影视分类
                    </Link>
                    <button
                      type="button"
                      onClick={() => setKeyword("")}
                      className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600 cursor-pointer whitespace-nowrap"
                    >
                      清空关键词
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-10 text-center">
            <h2 className="text-base font-medium text-foreground-950">输入关键词开始搜索</h2>
            <p className="mt-2 text-sm text-foreground-500">
              支持片名、演员、导演、题材与年份，例如「治愈」或「2024」。
            </p>
          </div>
        )}

        <section className="mt-12">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <h2 className="text-lg font-bold tracking-tight text-foreground-950">热门影视推荐</h2>
              <p className="mt-1 text-sm text-foreground-500">搜索没有头绪时，可以先从这些作品看起。</p>
            </div>
            <Link
              to="/category"
              className="hidden items-center gap-1 text-sm text-foreground-600 transition-colors hover:text-accent-600 cursor-pointer whitespace-nowrap sm:inline-flex"
            >
              查看全部
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
            {hot.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      </div>
    </PageLayout>
  );
}